import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import _ from 'lodash';
import cardTypeCheckers from '../../types/cardTypeCheckers';
import ReportCanvas from './ReportCanvas';
import useWindowSize from '../../hooks/useWindowSize';
import buildLayout, {
  buildHeadlessRefitLayout,
  getLayoutTier,
  LayoutTier,
  MIN_REFLOW_ROW_HEIGHT,
} from './buildLayout';
import HeadlessRefitContext from '../../contexts/HeadlessRefitContext';
import CanvasContext from '../../contexts/CanvasContext';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import DashboardContext from '../../contexts/DashboardContext';
import EmptyDashboard from './EmptyDashboard';
import isDefined from '../../isDefined';
import {
  CANVAS_VERTICAL_SNAPPING_POINTS,
  EXCLUDE_HEIGHT,
} from '../../constants';
import ReportContext from '../../contexts/ReportContext';
import WallboardContext from '../../contexts/WallboardContext';
import useCanvasWidth from './useCanvasWidth';
import {
  CANVAS_ITEM_MARGIN,
  CANVAS_PADDING_VERTICAL,
  HEADLESS_RENDER_CANVAS_HEIGHT,
  POPUP_REPORT_HEIGHT,
} from './canvasConstants';
import PopupInnerContext from '../../contexts/PopupInnerContext';

export const getCanvasMode = ({
  isMobile,
}: {
  isMobile: boolean;
}): CanvasMode => {
  if (isMobile) {
    return 'mobile';
  } else {
    return 'desktop';
  }
};

const ReportCanvasContainer = ({
  currentCanvas,
  setCurrentCanvas,
  isEditing,
  previewCanvasMode,
  previewFrameHeight,
  startEditing,
}: {
  currentCanvas: Canvas;
  setCurrentCanvas: React.Dispatch<React.SetStateAction<Canvas>>;
  isEditing: boolean;
  previewCanvasMode?: CanvasMode;
  // When set, pins row-height sizing to this height instead of measuring the
  // live viewport. Used by scaled previews (ScaledCanvasFrame / CanvasPreview)
  // that render the canvas detached from the normal screen layout.
  previewFrameHeight?: number;
  startEditing: () => void;
}) => {
  const { isPopup } = useContext(PopupInnerContext);
  const { dashboard } = useContext(DashboardContext);
  const { report, onAddVisualization, isHeadlessRender, isPdfExport } =
    useContext(ReportContext);
  const { isWallboard, dashboardZoom } = useContext(WallboardContext);
  const isDashboard = dashboard !== undefined;
  const { isMobile } = useWindowSize();
  const [cardOnTopI, setCardOnTopI] = useState<string | undefined>();
  const [draggingI, setDraggingI] = useState<string | undefined>();
  const { trackEvent } = useContext(AnalyticsContext);
  const [canvasRowHeight, setCanvasRowHeight] = useState<number>(
    Math.floor(
      ((isHeadlessRender ? HEADLESS_RENDER_CANVAS_HEIGHT : window.innerHeight) -
        300) /
        CANVAS_VERTICAL_SNAPPING_POINTS,
    ),
  );
  const { canvasWidth, canvasDivRef } = useCanvasWidth(currentCanvas);

  const canvasMode =
    previewCanvasMode ||
    getCanvasMode({
      isMobile,
    });
  // Wallboards, headless renders (PDFs) and edit mode always show the authored
  // layout; everything else reflows to the canvas's own width.
  const tier: LayoutTier =
    isWallboard || isHeadlessRender || isEditing
      ? 'full'
      : getLayoutTier({
          canvasWidth: canvasWidth ?? 0,
          canvasMode: previewCanvasMode,
        });
  const { cards } = currentCanvas;
  const cardsToUse = cards.filter((c) => Object.keys(c.layout).length > 1);
  const layout = buildLayout({
    cards: cardsToUse,
    isEditing,
    tier,
  });

  // --- Scheduled-report PDF: refit grid cards to their content ---------------
  // Matrix gadgets report their natural rendered height here (keyed by card id);
  // in the PDF path we resize each card's slot to fit and compact the canvas so
  // there is no whitespace band below the last row. Gated on isPdfExport so it
  // never affects interactive views or live wallboards.
  const [contentHeights, setContentHeights] = useState<Record<string, number>>(
    {},
  );
  const [hasMatrixRegistered, setHasMatrixRegistered] = useState(false);
  const [isRefitSettled, setIsRefitSettled] = useState(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout>>();

  const reportContentHeight = useCallback(
    (cardId: string, px: number | undefined) => {
      setHasMatrixRegistered(true);
      setContentHeights((prev) => {
        if (px === undefined) {
          if (prev[cardId] === undefined) {
            return prev;
          }
          const next = { ...prev };
          delete next[cardId];
          return next;
        }
        if (prev[cardId] === px) {
          return prev;
        }
        return { ...prev, [cardId]: px };
      });
    },
    [],
  );

  // Consider the refit "settled" once heights have been quiet for a short
  // window; the print-ready poll waits on this before capturing.
  useEffect(() => {
    if (!isPdfExport || !hasMatrixRegistered) {
      return;
    }
    setIsRefitSettled(false);
    if (settleTimer.current) {
      clearTimeout(settleTimer.current);
    }
    settleTimer.current = setTimeout(() => setIsRefitSettled(true), 250);
    return () => {
      if (settleTimer.current) {
        clearTimeout(settleTimer.current);
      }
    };
  }, [contentHeights, isPdfExport, hasMatrixRegistered]);

  // Chart-like cards (visualisations that fill their card) are eligible for the
  // aspect-ratio cap in the PDF refit; text/image/video/iframe/metric-list/
  // scorecard/goal cards keep their authored height. Matrices are excluded
  // automatically — they report a measured content height and refit to it.
  const chartCapCardIds = useMemo(
    () =>
      new Set(
        cards
          .filter(
            (c) =>
              cardTypeCheckers.isReportGadget(c) ||
              cardTypeCheckers.isChartDefinition(c) ||
              cardTypeCheckers.isDashboardGadget(c),
          )
          .map((c) => String(c.layout.i)),
      ),
    [cards],
  );

  const displayLayout = isPdfExport
    ? buildHeadlessRefitLayout({
        layout,
        contentHeights,
        canvasRowHeight,
        canvasWidth: canvasWidth ?? 0,
        chartCapCardIds,
      })
    : layout;

  // Only ever pending once a matrix has registered, so chart-only reports never
  // block the print-ready signal.
  const isRefitPending =
    !!isPdfExport && hasMatrixRegistered && !isRefitSettled;

  const onResize = useCallback(() => {
    const idPostFix = (() => {
      if (report) {
        return report.id;
      }

      if (dashboard) {
        return dashboard.id;
      }
    })();

    const excludeHeights = document.getElementsByClassName(
      `${EXCLUDE_HEIGHT}-${idPostFix}`,
    );
    let heightReduction = 0;
    for (let i = 0; i < excludeHeights.length; i++) {
      heightReduction += excludeHeights[i].clientHeight;
    }

    const reportHeight = (() => {
      // Headless PDF render: size rows against a fixed reference height so the
      // tall capture viewport doesn't stretch every gadget (see
      // HEADLESS_RENDER_CANVAS_HEIGHT).
      if (isHeadlessRender) {
        return HEADLESS_RENDER_CANVAS_HEIGHT;
      }

      const r = document.getElementById(
        dashboard && !isPopup ? 'dashboard' : 'report',
      );

      if (isPopup) {
        if (!r) {
          return (
            document.body.clientHeight *
            POPUP_REPORT_HEIGHT.CANVAS_SIZE_MULTIPLIER
          );
        }
        return r.clientHeight;
      }

      if (r) {
        return r.clientHeight;
      }

      return document.body.clientHeight;
    })();
    const canvasHeight =
      previewFrameHeight ||
      (reportHeight - heightReduction) / (isWallboard ? dashboardZoom : 1);
    const marginsPerPage = CANVAS_VERTICAL_SNAPPING_POINTS - 1;
    const rowHeight =
      (canvasHeight -
        marginsPerPage * CANVAS_ITEM_MARGIN -
        CANVAS_PADDING_VERTICAL * 2) /
      CANVAS_VERTICAL_SNAPPING_POINTS;

    setCanvasRowHeight(rowHeight);
  }, [
    dashboard,
    isPopup,
    isWallboard,
    report,
    dashboardZoom,
    isHeadlessRender,
    previewFrameHeight,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
    // We want this to trigger when isEditing changes
  }, [onResize, isEditing]);

  // The filter bar can grow in height when its content wraps (e.g. many quick
  // filter pills). window resize events don't fire in that case, so we use a
  // ResizeObserver on every EXCLUDE_HEIGHT element to catch height changes and
  // recalculate canvas row height whenever the bar grows or shrinks.
  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }
    const idPostFix = report ? report.id : dashboard?.id;
    if (!idPostFix) return;

    const observer = new ResizeObserver(onResize);
    const elements = document.getElementsByClassName(
      `${EXCLUDE_HEIGHT}-${idPostFix}`,
    );
    for (let i = 0; i < elements.length; i++) {
      observer.observe(elements[i]);
    }
    return () => observer.disconnect();
  }, [onResize, report, dashboard]);

  const onLayoutChange = useCallback(
    (newLayout: GridLayout.Layout[]) => {
      const sanitizeLayout = (layout: GridLayout.Layout[]) => {
        return layout.filter((l) => l.i !== undefined && l.i !== 'null');
      };

      if (
        !isEditing ||
        _.isEqual(sanitizeLayout(newLayout), sanitizeLayout(layout))
      ) {
        return;
      }

      if (isDashboard) {
        trackEvent('Dashboard - Edit - Card Moved', {
          isWallboardSlide: isWallboard ? 'true' : 'false',
        });
      } else {
        trackEvent('Report - Edit - Card Moved');
      }

      setCurrentCanvas(
        (can) =>
          ({
            ...can,
            cards: can.cards
              .map((c) => {
                const l = newLayout.find((l) => l.i === c.layout.i);
                if (!l) {
                  return undefined;
                }
                return {
                  ...c,
                  layout: {
                    ...l,
                    static: false,
                  },
                } as CanvasCard.Card;
              })
              .filter(isDefined),
          }) as Canvas,
      );
    },
    [isDashboard, isEditing, isWallboard, layout, setCurrentCanvas, trackEvent],
  );

  const removeGadget = (i: string) => {
    if (isDashboard) {
      trackEvent('Dashboard - Edit - Card Removed', {
        isWallboardSlide: isWallboard ? 'true' : 'false',
      });
    } else {
      trackEvent('Report - Edit - Card Removed');
    }

    const newCards = currentCanvas.cards
      .filter((c) => {
        return c.layout.i !== i;
      })
      .filter((c) => Object.keys(c.layout).length > 1);

    setCurrentCanvas({
      ...currentCanvas,
      cards: newCards,
    });
  };

  const onResizeStart = (layout: Layout, oldItem: any, newItem: any) => {
    setDraggingI(newItem.i);
  };

  const onResizeStop = () => {
    setDraggingI(undefined);
  };

  const updateCard = useCallback(
    (newCard: CanvasCard.Card) => {
      setCurrentCanvas((can) => ({
        ...can,
        cards: can.cards
          .map((c: CanvasCard.Card) => {
            if (c.layout.i === newCard.layout.i) {
              return newCard;
            }
            return c;
          })
          .filter(isDefined)
          .filter((c: CanvasCard.Card) => Object.keys(c.layout).length > 1),
      }));
    },
    [setCurrentCanvas],
  );

  if (cards.length === 0) {
    return (
      <EmptyDashboard
        startEditing={startEditing}
        setCurrentCanvas={setCurrentCanvas}
        canvasMode={canvasMode}
        onCreateNewGadgetClicked={!isDashboard ? onAddVisualization : undefined}
      />
    );
  }

  return (
    <div
      style={{
        position: 'relative',
      }}
      ref={canvasDivRef}
      data-headless-refit-pending={isRefitPending ? 'true' : undefined}
    >
      <CanvasContext.Provider
        value={{
          cardOnTopI,
          setCardOnTopI,
          isEditing,
          canvas: currentCanvas,
          updateCard,
        }}
      >
        <HeadlessRefitContext.Provider value={{ reportContentHeight }}>
          {canvasWidth !== undefined && (
            <ReportCanvas
              cards={cardsToUse}
              layout={displayLayout}
              unrefitLayout={layout}
              onLayoutChange={onLayoutChange}
              removeGadget={removeGadget}
              isEditing={isEditing}
              cardOnTopI={cardOnTopI}
              tier={tier}
              onResizeStart={onResizeStart}
              onResizeStop={onResizeStop}
              draggingI={draggingI}
              canvasRowHeight={
                tier === 'full'
                  ? canvasRowHeight
                  : Math.max(canvasRowHeight, MIN_REFLOW_ROW_HEIGHT)
              }
              canvasWidth={canvasWidth}
            />
          )}
        </HeadlessRefitContext.Provider>
      </CanvasContext.Provider>
    </div>
  );
};

export default ReportCanvasContainer;
